import React, { useState } from 'react';
import { updateMatchScore, createMatch, getLeagueMatches } from '../../services/leagueService';
import { generateNextRoundMatches } from '../../utils/scheduleGenerator';
import { IconCircleX } from '@tabler/icons-react';

const ScoreEntryModal = ({ leagueId, match, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    participant1Score: match.participant1Score || 0,
    participant2Score: match.participant2Score || 0
  });
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.participant1Score < 0 || formData.participant2Score < 0) {
      setError('Scores must be 0 or greater');
      return;
    }

    if (formData.participant1Score === formData.participant2Score) {
      setError('Scores cannot be tied. Please enter a winner.');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      const winnerId = formData.participant1Score > formData.participant2Score
        ? match.participant1Id
        : match.participant2Id;

      const winnerName = formData.participant1Score > formData.participant2Score
        ? match.participant1Name
        : match.participant2Name;

      // Update the match score
      await updateMatchScore(leagueId, match.id, {
        participant1Score: formData.participant1Score,
        participant2Score: formData.participant2Score,
        winnerId,
        status: 'completed'
      });

      // Get all matches to check if round is complete
      const allMatches = await getLeagueMatches(leagueId);

      // Create updated match object with new score
      const updatedMatch = {
        ...match,
        participant1Score: formData.participant1Score,
        participant2Score: formData.participant2Score,
        winnerId,
        winnerName,
        status: 'completed'
      };

      // Update the matches array with the completed match
      const updatedMatches = allMatches.map(m =>
        m.id === match.id ? updatedMatch : m
      );

      // Check if we need to generate next round matches
      const nextRoundMatches = generateNextRoundMatches(updatedMatches, updatedMatch);

      if (nextRoundMatches && nextRoundMatches.length > 0) {
        // Create next round matches
        for (const nextMatch of nextRoundMatches) {
          await createMatch(leagueId, nextMatch);
        }
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error updating match score:', err);
      setError('Failed to update score. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <section className="ls-score-entry-modal">
      <section className="ls-modal-content">
        <header className="ls-modal-header">
          <h2>Enter Match Score</h2>
          <button onClick={onClose} className="ls-close-btn" aria-label="Close modal">
            <IconCircleX size={24} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="ls-score-entry-form">
          <div className="ls-match-info">
            <p className="ls-match-round">Round {match.round}</p>
            {match.scheduledTime && (
              <p className="ls-match-time">
                {new Date(match.scheduledTime).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
            )}
            {match.location && (
              <p className="ls-match-location">{match.location}</p>
            )}
          </div>

          <div className="ls-score-inputs">
            <div className="ls-participant-score">
              <label htmlFor="participant1Score">{match.participant1Name}</label>
              <input
                type="number"
                id="participant1Score"
                name="participant1Score"
                value={formData.participant1Score}
                onChange={handleInputChange}
                min="0"
                required
                autoFocus
              />
            </div>

            <div className="ls-vs-divider">
              <span>VS</span>
            </div>

            <div className="ls-participant-score">
              <label htmlFor="participant2Score">{match.participant2Name}</label>
              <input
                type="number"
                id="participant2Score"
                name="participant2Score"
                value={formData.participant2Score}
                onChange={handleInputChange}
                min="0"
                required
              />
            </div>
          </div>

          {error && (
            <section className="ls-error-message" role="alert">{error}</section>
          )}

          <footer className="ls-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="ls-btn-secondary"
              disabled={updating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ls-btn-primary"
              disabled={updating}
            >
              {updating ? 'Saving...' : 'Save Score'}
            </button>
          </footer>
        </form>
      </section>
    </section>
  );
};

export default ScoreEntryModal;
